<script setup lang="ts">
/**
 * Заметки плана — макеты 9-5 (304:1102) и 9-7 (304:15572).
 *
 * Мысль записывают одним полем: ни проекта, ни типа, ни срока. Каждое поле,
 * без которого не сохранить, — это повод не записать вовсе, а заметку берут
 * посреди разговора, когда думают не про формы.
 *
 * Разбирает мысль ассистент: кнопка «В задачи» открывает панель с готовой
 * командой, та собирает черновик пачки, и после его применения заметка
 * помечается разобранной с ключами вышедших из неё задач. Отдельной кнопки
 * «создать задачу вручную» здесь нет намеренно — она вернула бы ровно ту форму,
 * от которой заметка и уводит.
 *
 * Мит-режим (9-7) — та же лента во весь экран с крупным полем: во время созвона
 * пишут подряд, и колонка в 384 пикселя рядом со стеком для этого узка.
 */
import { AppUserPicker } from '@vinprop/design-system'
import { useTextareaAutosize } from '@vueuse/core'
import { Maximize2, Plus, Sparkles, X } from 'lucide-vue-next'
import { computed, nextTick, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { authApi } from '@/features/auth/api'
import { useAuthStore } from '@/features/auth/store'
import { useAssistantStore } from '@/stores/assistant'

import { useMeetingStore } from '../meetingStore'
import { formatNoteStamp, type Note } from '../notes'
import { useNotesStore } from '../notesStore'

import type { Person } from '@/features/auth/types'

const props = defineProps<{
  /** Мит-режим: лента во весь экран и крупное поле ввода (макет 9-7) */
  meeting?: boolean
}>()

const emit = defineEmits<{ expand: [] }>()

const { t, locale } = useI18n()
const notes = useNotesStore()
const assistant = useAssistantStore()
const meetingStore = useMeetingStore()
const auth = useAuthStore()

/**
 * Поле растёт под текст: в мит-режиме пишут абзацами, а однострочное поле
 * прячет начало мысли, стоит перевалить за строку (DEV-1947). Потолок в десять
 * строк оставляет ленту на экране — дальше поле скроллится внутри себя.
 */
const { textarea: field, input: draft } = useTextareaAutosize()

const stamp = (note: Note) =>
  formatNoteStamp(note.createdAt, locale.value, {
    today: t('plan.notes.today'),
    yesterday: t('plan.notes.yesterday'),
  })

const save = () => {
  const text = draft.value.trim()

  if (!text) return

  draft.value = ''
  void notes.add(text)
}

/** Кого зовут на встречу: справочник людей — право `users:read` */
const people = ref<Person[]>([])
const isDirectoryOpen = ref(true)
const invitee = ref('')

/**
 * Счётчик перерисовок пикера.
 *
 * Приглашение — действие, а не поле: после него в строке не должно оставаться
 * имени позванного, иначе следующего зовут поверх чужого текста. Сброса
 * `modelValue` для этого мало — своё состояние поиска компонент держит сам.
 */
const pickerKey = ref(0)

const loadPeople = async () => {
  if (people.value.length || !isDirectoryOpen.value) return

  try {
    people.value = await authApi.listUsers()
  } catch {
    // Без права на справочник звать некого — пикер честно говорит об этом, а
    // не притворяется пустым списком.
    isDirectoryOpen.value = false
  }
}

/**
 * Позвать человека. Первое приглашение и заводит встречу — до него заметки
 * личные, и задним числом они общими не становятся.
 */
const invite = async (userId: string) => {
  if (!userId) return

  invitee.value = ''
  pickerKey.value += 1
  await meetingStore.invite([userId])

  const session = meetingStore.session

  if (session) {
    await notes.setSession(session.id)
    meetingStore.openStream(session.id)
  }
}

const endMeeting = async () => {
  if (!window.confirm(t('plan.meeting.endConfirm'))) return

  await meetingStore.end()
  meetingStore.closeStream()
  await notes.setSession('')
}

/** Правка заметки прямо в карточке — только на встрече, где протокол общий */
const editingId = ref('')
const editDraft = ref('')

const canEdit = (note: Note): boolean => {
  if (!note.sessionId) return false
  // Пока встреча идёт, протокол правят вместе; после — только свои слова.
  return meetingStore.isActive || note.authorId === auth.user?.id
}

const startEdit = (note: Note) => {
  if (!canEdit(note)) return

  editingId.value = note.id
  editDraft.value = note.body
}

const applyEdit = async () => {
  const id = editingId.value

  if (!id) return

  const text = editDraft.value
  editingId.value = ''

  await notes.edit(id, text)
}

const canRemove = (note: Note): boolean => !note.sessionId || canEdit(note)

/**
 * Enter сохраняет, Shift+Enter переносит строку.
 *
 * Кнопки «сохранить» нет: в мит-режиме пишут вслепую, слушая разговор, и
 * дотягиваться до кнопки после каждой мысли значит потерять следующую.
 */
const onEnter = (event: KeyboardEvent) => {
  if (event.shiftKey) return

  event.preventDefault()
  save()
}

const focus = async () => {
  await nextTick()
  field.value?.focus()
}

/** Сколько заметки видно в команде ассистенту: она ищет её сама, это примета */
const EXCERPT = 80

/**
 * Разобрать заметку. Команда уходит в панель готовой (паттерн `commands.ts`):
 * человек нажал кнопку у конкретной карточки, и переспрашивать его «какую
 * именно» — значит заставить пересказать то, что он уже показал пальцем.
 */
const parse = (note: Note) => {
  notes.startParsing(note.id)
  assistant.open()

  const excerpt = note.body.slice(0, EXCERPT).trim()

  void assistant.send(t('plan.notes.parse.prompt', { text: excerpt }))
}

const asKeys = (result: unknown): string[] => {
  const issues = (result as { issues?: { key?: string }[] } | null)?.issues

  return Array.isArray(issues) ? issues.map((issue) => issue.key ?? '').filter(Boolean) : []
}

/** Последняя пачка задач, созданная ассистентом в этом разговоре */
const created = computed(() => {
  for (let index = assistant.items.length - 1; index >= 0; index--) {
    const item = assistant.items[index]

    if (item?.kind !== 'call' || item.name !== 'create_issues_batch' || item.state !== 'done') {
      continue
    }

    return { callId: item.callId, keys: asKeys(item.result) }
  }

  return null
})

/**
 * Пачка создана — заметка разобрана.
 *
 * Пометку ставит фронт, а не бэкенд: инструмент создания задач ничего не знает
 * про заметки, и учить его этому значило бы связать «создай задачи» с «закрой
 * заметку» навсегда — включая случаи, когда задачи создают вообще без неё.
 *
 * Разобранные вызовы запоминаются: события панели переигрываются при каждой
 * перерисовке, и без этого одна и та же пачка помечала бы заметку по кругу.
 */
const handled = new Set<string>()

watch(created, (batch) => {
  const id = notes.parsingId

  if (!batch || !id || !batch.keys.length || handled.has(batch.callId)) return

  handled.add(batch.callId)
  void notes.markProcessed(id, batch.keys)
})

defineExpose({ focus })

// Мит-режим открывается ради того, чтобы писать: курсор уже в поле.
watch(
  () => props.meeting,
  (on) => {
    if (on) void focus()
  },
  { immediate: true },
)
</script>

<template>
  <section class="notes" :class="{ 'notes--meeting': meeting }">
    <header class="notes__head">
      <h2 class="notes__title">{{ t('plan.notes.title') }}</h2>

      <span v-if="meeting" class="notes__mode">{{ t('plan.notes.meeting') }}</span>

      <span v-if="meeting" class="notes__hint">{{ t('plan.notes.escape') }}</span>

      <template v-else>
        <!-- Мит-режима нет ни на одном фрейме: во весь экран ленту разворачивают
             отсюда, иначе к макету 9-7 нет ни одной двери. -->
        <button
          type="button"
          class="notes__icon"
          :title="t('plan.notes.meeting')"
          :aria-label="t('plan.notes.meeting')"
          @click="emit('expand')"
        >
          <Maximize2 :size="16" />
        </button>

        <button
          type="button"
          class="notes__icon"
          :title="t('plan.notes.add')"
          :aria-label="t('plan.notes.add')"
          @click="focus()"
        >
          <Plus :size="16" />
        </button>
      </template>
    </header>

    <!-- Встреча: кто на ней и чем она кончается. Только в мит-режиме — в
         колонке шириной 384 пикселя этому ряду места нет. -->
    <div v-if="meeting" class="meet">
      <template v-if="meetingStore.isActive">
        <span class="meet__label">{{ t('plan.meeting.participants') }}</span>

        <!-- Позванные показываются приглушённо: без них пригласивший не видит,
             ушло приглашение или нет — участником человек станет, только когда
             придёт. -->
        <span
          v-for="participant in meetingStore.everyone"
          :key="participant.userId"
          class="meet__person"
          :class="{ 'meet__person--waiting': !participant.joined }"
          :title="participant.joined ? participant.name : t('plan.meeting.waiting')"
        >
          {{ participant.name || '…' }}
        </span>

        <span v-if="meetingStore.isOffline" class="meet__offline">
          {{ t('plan.meeting.offline') }}
        </span>
      </template>

      <span v-else class="meet__label">{{ t('plan.meeting.solo') }}</span>

      <div class="meet__actions">
        <AppUserPicker
          :key="pickerKey"
          v-model="invitee"
          class="meet__picker"
          :users="people"
          :placeholder="t('plan.meeting.invite')"
          :empty-text="t('plan.meeting.nobody')"
          :unavailable="!isDirectoryOpen"
          :unavailable-text="t('plan.meeting.noDirectory')"
          @click="loadPeople()"
          @update:model-value="invite"
        />

        <button
          v-if="meetingStore.isAuthor"
          type="button"
          class="meet__end"
          @click="endMeeting()"
        >
          {{ t('plan.meeting.end') }}
        </button>
      </div>
    </div>

    <div class="notes__field">
      <textarea
        ref="field"
        v-model="draft"
        class="notes__input"
        :placeholder="meeting ? t('plan.notes.bigPlaceholder') : t('plan.notes.placeholder')"
        :aria-label="t('plan.notes.placeholder')"
        @keydown.enter="onEnter"
      />

      <p v-if="meeting" class="notes__keys">{{ t('plan.notes.keys') }}</p>
    </div>

    <p v-if="notes.isLoaded && !notes.items.length" class="notes__empty">
      {{ t('plan.notes.empty') }}
    </p>

    <article v-for="note in notes.items" :key="note.id" class="note">
      <!-- Правка на месте: описку в протоколе исправляют по ходу разговора, и
           отдельный экран ради одной строки сбил бы с мысли. -->
      <textarea
        v-if="editingId === note.id"
        v-model="editDraft"
        class="note__edit"
        rows="3"
        :aria-label="t('plan.notes.edit')"
        @keydown.enter.exact.prevent="applyEdit()"
        @keydown.esc="editingId = ''"
        @blur="applyEdit()"
      />

      <p
        v-else
        class="note__body"
        :class="{ 'note__body--processed': note.processedAt }"
        @dblclick="startEdit(note)"
      >
        {{ note.body }}
      </p>

      <div class="note__meta">
        <span class="note__time">{{ stamp(note) }}</span>

        <!-- Подпись автора — только у сессионной: в личной ленте автор один. -->
        <span v-if="note.sessionId && note.authorName" class="note__author">
          {{ note.authorName }}
        </span>

        <template v-if="note.processedAt">
          <span class="note__done">✓ {{ t('plan.notes.processed') }}</span>

          <RouterLink
            v-for="key in note.issueKeys"
            :key="key"
            class="note__chip"
            :to="`/issue/${key}`"
          >
            {{ key }}
          </RouterLink>
        </template>

        <button v-else type="button" class="note__parse" @click="parse(note)">
          <Sparkles :size="12" aria-hidden="true" />
          {{ t('plan.notes.parse.action') }}
        </button>
      </div>

      <!-- Удаления нет в макетах, а описка в заметке случается чаще всего
           прочего: кнопка появляется по наведению и раскладку не двигает. -->
      <button
        v-if="canRemove(note)"
        type="button"
        class="note__remove"
        :title="t('plan.notes.remove')"
        :aria-label="t('plan.notes.remove')"
        @click="notes.remove(note.id)"
      >
        <X :size="14" />
      </button>
    </article>
  </section>
</template>

<style scoped>
.notes {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.notes--meeting {
  gap: 14px;
  width: 100%;
  max-width: 720px;
  margin: 0 auto;
}

.notes__head {
  display: flex;
  align-items: center;
  gap: 8px;
}

.notes__title {
  margin: 0;
  color: var(--color-text-primary);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  line-height: 22px;
}

.notes--meeting .notes__title {
  font-size: 20px;
  letter-spacing: -0.2px;
  line-height: 26px;
}

.notes__mode {
  color: var(--color-primary);
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-medium);
  line-height: 16px;
}

.notes__hint {
  margin-left: auto;
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
  line-height: 16px;
}

.notes__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: var(--radius-sm);
  background: none;
  color: var(--color-text-muted);
  cursor: pointer;
}

.notes__icon:first-of-type {
  margin-left: auto;
}

.notes__icon:hover {
  color: var(--color-text-primary);
}

.notes__field {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
}

/* Крупное поле мит-режима: рамка цветом плана — писать сюда и есть работа */
.notes--meeting .notes__field {
  padding: 14px 16px;
  border-color: var(--color-primary);
  /* 12 из макета: между --radius-md (10) и --radius-lg (16) токена нет */
  border-radius: 12px;
}

.notes__input {
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-text-primary);
  font-family: inherit;
  font-size: var(--font-size-body-sm);
  line-height: 18px;
  resize: none;
}

.notes__input:focus {
  outline: none;
}

.notes__input::placeholder {
  color: var(--color-text-muted);
}

/* Пять строк минимум, десять максимум: ниже пяти поле прячет начало мысли,
   выше десяти — выталкивает ленту за экран (DEV-1947). Дальше поле скроллится
   внутри себя. */
.notes--meeting .notes__input {
  min-height: 100px;
  max-height: 200px;
  overflow-y: auto;
  font-size: var(--font-size-body);
  line-height: 20px;
}

/* Ряд встречи: кто на ней, кого позвать, чем закончить */
.meet {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding-bottom: 2px;
}

.meet__label {
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
  line-height: 16px;
}

.meet__person {
  padding: 2px 8px;
  border-radius: 10px;
  background: var(--color-primary-tint);
  color: var(--color-primary);
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-medium);
  line-height: 16px;
}

/* Позвали, но человек ещё не пришёл: приглашение ушло, участником он не стал */
.meet__person--waiting {
  background: var(--color-bg-muted);
  color: var(--color-text-muted);
}

/* Связь оборвалась: чужие заметки сейчас не приходят, и молчать об этом
   нельзя — пустая лента выглядит как «никто ничего не пишет». */
.meet__offline {
  color: var(--color-danger);
  font-size: var(--font-size-caption);
  line-height: 16px;
}

.meet__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.meet__picker {
  min-width: 200px;
}

.meet__end {
  padding: 4px 10px;
  white-space: nowrap;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  background: none;
  color: var(--color-text-secondary);
  font-family: inherit;
  font-size: var(--font-size-caption);
  line-height: 16px;
  cursor: pointer;
}

.meet__end:hover {
  border-color: var(--color-danger);
  color: var(--color-danger);
}

.note__edit {
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-text-primary);
  font-family: inherit;
  font-size: var(--font-size-body);
  line-height: 20px;
  resize: none;
}

.note__edit:focus {
  outline: none;
}

.note__author {
  color: var(--color-text-secondary);
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-medium);
  line-height: 16px;
}

.notes__keys {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
  line-height: 16px;
}

.notes__empty {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
  line-height: 16px;
}

.note {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
}

.note__body {
  margin: 0;
  color: var(--color-text-primary);
  font-size: var(--font-size-body);
  line-height: 20px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

/* Разобранная заметка остаётся в ленте, но уступает свежим */
.note__body--processed {
  color: var(--color-text-secondary);
}

.note__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.note__time {
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
  line-height: 16px;
}

.note__done {
  margin-left: auto;
  color: var(--color-success-strong);
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-medium);
  line-height: 16px;
}

.note__chip {
  padding: 2px 6px;
  border-radius: 5px;
  background: var(--color-bg-muted);
  color: var(--color-text-secondary);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-medium);
  line-height: 16px;
  text-decoration: none;
}

.note__parse {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-left: auto;
  padding: 4px 8px;
  /* 6 — радиус чипа из макета; токена ровно на него нет, ближайшие 8 и 10
     заметно круглее на кнопке высотой 24. */
  border: none;
  border-radius: 6px;
  background: var(--color-primary-tint);
  color: var(--color-primary);
  font-family: inherit;
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-medium);
  line-height: 16px;
  cursor: pointer;
}

.note__remove {
  position: absolute;
  top: 8px;
  right: 8px;
  display: none;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: none;
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-text-muted);
  cursor: pointer;
}

.note__remove:hover {
  color: var(--color-danger);
}

.note:hover .note__remove,
.note__remove:focus-visible {
  display: inline-flex;
}
</style>
